import React from "react";
import {
  Grid,
  makeStyles,
  Typography,
  Paper,
  Button,
  IconButton,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from "@material-ui/core";
import { ValidatorForm, TextValidator } from 'react-material-ui-form-validator';
import useError from "Hooks/useError";
import MuiAlert from "@material-ui/lab/Alert";
import { message } from "constants/message";
import {
  MuiPickersUtilsProvider,
  DateTimePicker,
} from "@material-ui/pickers";
import ClearIcon from "@material-ui/icons/Clear";
import DateFnsUtils from "@date-io/date-fns";

import "dayjs/locale/ja";
import dayjs from "dayjs";
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
dayjs.locale("ja");
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);


const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
    display: "flex",
    overflow: "auto",
    flexDirection: "column",
  },
  wrapper: {
    marginTop: theme.spacing(2),
  },
  dateWrapper: {
    display: "flex",
    marginTop: theme.spacing(2),
  },
  selectFormControl: {
    width: "125px"
  },
  divider: {
    display: "flex",
    alignItems: "center",
    margin: theme.spacing(2),
  },
  clearButton: {
    width: "32px",
    height: "32px",
  },
  messsageWrapper: {
    marginTop: theme.spacing(1),
  },
}));

export default function Form({ submit, information = null, isEdit = false }) {
  const classes = useStyles();
  const stateError = useError();
  const messages = message();

  const [validFrom, setValidFrom] = React.useState(dayjs().format("YYYY-MM-DD 00:00:00"));
  const [validUntil, setValidUntil] = React.useState(null);
  const [content, setContent] = React.useState("");
  const [url, setUrl] = React.useState("");
  const [display, setDisplay] = React.useState({
    before_login: true,
    after_login: true,
  });

  React.useEffect(() => {
    stateError.initialize();
  }, []);

  React.useEffect(() => {
    if (!information) return;
    setValidFrom(information.validFrom);
    setValidUntil(information.validUntil);
    setContent(information.content);
    setUrl(information.url);
    setDisplay(information.display);
  }, [information]);

  const handleDisplayChange = (event) => {
    setDisplay({ ...display, [event.target.name]: event.target.checked });
  };

  const dateValidation = (date, type) => {
    // 入力内容が日付として正しいかチェック
    if (dayjs(date, "YYYY/MM/DD HH:mm:ss").isValid()) {
      if (type == "validFrom") {
        const dateTime = dayjs(validUntil).format("YYYY-MM-DD HH:mm:ss")
        const selectedDateTime = dayjs(date).format("YYYY-MM-DD HH:mm:ss")
        // 比較対象がnullなため比較なしでset
        // 終了日付が開始日付より後の日かチェック
        if (!dayjs(dateTime).isValid() || (dayjs(dateTime).isSameOrAfter(dayjs(selectedDateTime)))) {
          setValidFrom(dayjs(date).format("YYYY-MM-DD HH:mm:ss"));
        } else {
          alert(messages.validation.dateRang);
        }
      } else if (type == "validUntil"){
        const dateTime = dayjs(validFrom).format("YYYY-MM-DD HH:mm:ss")
        const selectedDateTime = dayjs(date).format("YYYY-MM-DD HH:mm:ss")
        // 比較対象がnullなため比較なしでset
        // 開始日付が終了日付より前の日かチェック
        if (!dayjs(dateTime).isValid() || (dayjs(dateTime).isSameOrBefore(dayjs(selectedDateTime)))) {
          setValidUntil(dayjs(date).format("YYYY-MM-DD HH:mm:ss"));
        } else {
          alert(messages.validation.dateRang);
        }
      }
      return;
    }
    return;
  }

  function clearValidUntil(e) {
    e.stopPropagation();
    setValidUntil(null);
  }

  const onError = () => {
    stateError.dispatch({
      type: "set",
      payload: {
        isError: true,
        errorMsg: messages.validation.common,
      },
    });
  };

  return (
    <Grid>
      <ValidatorForm
        onSubmit={(e) => {
          e.preventDefault();
          submit(
            {
              validFrom,
              validUntil,
              content,
              url,
              display,
            }
          );
        }}
        onError={onError}
      >
        <Paper elevation={3} className={classes.paper}>
          <Typography variant="h6" color="primary">
            {isEdit ? "お知らせ更新" : "お知らせ登録"}
          </Typography>
          <Grid container>
            <Grid item xs={12} className={classes.dateWrapper}>
              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <DateTimePicker
                  label="掲載開始日時"
                  format="yyyy/MM/dd HH:mm:ss"
                  value={validFrom}
                  onChange={(date) => dateValidation(date, "validFrom")}
                />
              </MuiPickersUtilsProvider>
              <div className={classes.divider}>〜</div>
              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <DateTimePicker
                  label="掲載終了日時"
                  format="yyyy/MM/dd HH:mm:ss"
                  value={validUntil}
                  onChange={(date) => dateValidation(date, "validUntil")}
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        className={classes.clearButton}
                        onClick={(e) => clearValidUntil(e)}>
                          <ClearIcon />
                      </IconButton>
                    )
                  }}
                />
              </MuiPickersUtilsProvider>
            </Grid>
            <Grid item xs={12} className={classes.wrapper}>
              <TextValidator
                label="掲載内容*"
                fullWidth
                value={content}
                onChange={(event) => {
                  setContent(event.target.value);
                }}
                type="text"
                validators={['required']}
                errorMessages={[
                  messages.validation.required,
                ]}
              />
            </Grid>
            <Grid item xs={12} className={classes.wrapper}>
              <TextValidator
                label="リンク先URL"
                fullWidth
                value={url}
                onChange={(event) => {
                  setUrl(event.target.value);
                }}
                type="text"
              />
            </Grid>
          </Grid>
          <Grid item xs={12} className={classes.wrapper}>
            <FormControl component="fieldset">
              <FormLabel>表示設定</FormLabel>
              <FormGroup>
                <FormControlLabel
                  control={<Checkbox checked={display.before_login} onChange={handleDisplayChange} name="before_login" />}
                  label="ログイン画面に表示する"
                />
                <FormControlLabel
                  control={<Checkbox checked={display.after_login} onChange={handleDisplayChange} name="after_login" />}
                  label="ヘッダに表示する（ヘッダに表示される通知「お知らせがあります」をクリックすると一覧ポップアップで表示）"
                />
              </FormGroup>
            </FormControl>
          </Grid>
        </Paper>
        {stateError.state.isError && (
          <div className={classes.messsageWrapper}>
            <MuiAlert
              elevation={6}
              variant="filled"
              severity="error"
              className={classes.toast}
            >
              {stateError.state.errorMsg}
            </MuiAlert>
          </div>
        )}
        <Grid container justifyContent="center" className={classes.wrapper}>
          <Button type="submit" variant="contained" color="primary">
            {isEdit ? "更新" : "登録"}
          </Button>
        </Grid>
      </ValidatorForm>
    </Grid>
  );
}
